import { BarChart2, CheckCircle, Filter, Pencil, Plus, Search, Star, Trash2, XCircle } from "lucide-react";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ISignal, EventType, ImportanceLevel } from "../../../interfaces/signal";
import {
    getAdminSignals,
    deleteAdminSignal,
    toggleSignalActive,
    getAdminSignalStats,
    AdminSignalFilters,
} from "../../../interfaces/admin/signals";
import SignalRatingsModal from "./SignalRatingsModal";
import BONavigation from "../../../components/admin/BONavigation";

export default function AdminSignals() {
    const navigate = useNavigate();
    const [signals, setSignals] = useState<ISignal[]>([]);
    const [stats, setStats] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [showFilters, setShowFilters] = useState(false);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [selectedSignalForRatings, setSelectedSignalForRatings] = useState<ISignal | null>(null);

    const [filters, setFilters] = useState<AdminSignalFilters>({
        sortBy: "createdAt",
        order: "desc",
        isActive: "all",
        includeExpired: false,
    });

    useEffect(() => {
        fetchData();
        fetchStats();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters, page]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const response = await getAdminSignals({ 
                ...filters, 
                search: searchTerm || undefined, 
                page, 
                limit: 20 
            });
            
            if (page === 1) {
                setSignals(response.data || []);
            } else {
                setSignals(prev => [...prev, ...(response.data || [])]);
            }
            
            setHasMore(response.data?.length === 20);
        } catch (err) {
            console.error("Failed to fetch signals:", err);
            setError("Failed to load signals");
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            const statsData = await getAdminSignalStats();
            setStats(statsData);
        } catch (err) {
            console.error("Failed to fetch stats:", err);
        }
    };

    const handleSearch = () => {
        setPage(1);
        fetchData();
    };

    const handleFilterChange = (key: keyof AdminSignalFilters, value: any) => {
        setPage(1);
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    const handleDelete = async (signalId: string) => {
        if (!window.confirm("Are you sure you want to delete this signal?")) {
            return;
        }

        try {
            await deleteAdminSignal(signalId);
            setSignals(prev => prev.filter(s => s._id !== signalId));
            fetchStats();
        } catch (err) {
            console.error("Failed to delete signal:", err);
            alert("Failed to delete signal");
        }
    };

    const handleToggleActive = async (signalId: string) => {
        try {
            const updated = await toggleSignalActive(signalId);
            setSignals(prev => prev.map(s => s._id === signalId ? updated : s));
            fetchStats();
        } catch (err) {
            console.error("Failed to toggle signal:", err);
            alert("Failed to toggle signal status");
        }
    };

    const loadMore = () => {
        setPage(prev => prev + 1);
    };

    const eventTypes: { value: EventType; label: string }[] = [
        { value: "seed", label: "Seed" },
        { value: "series_a", label: "Series A" },
        { value: "series_b", label: "Series B" },
        { value: "series_c", label: "Series C" },
        { value: "series_d", label: "Series D" },
        { value: "ipo", label: "IPO" },
        { value: "acquisition", label: "Acquisition" },
        { value: "merger", label: "Merger" },
        { value: "partnership", label: "Partnership" },
        { value: "expansion", label: "Expansion" },
        { value: "product_launch", label: "Product Launch" },
        { value: "hiring", label: "Hiring" },
        { value: "executive_change", label: "Executive Change" },
        { value: "other", label: "Other" },
    ];

    const importanceLevels: { value: ImportanceLevel; label: string }[] = [
        { value: "low", label: "Low" },
        { value: "medium", label: "Medium" },
        { value: "high", label: "High" },
        { value: "critical", label: "Critical" },
    ];

    const formatDate = (dateString?: string) => {
        if (!dateString) return "N/A";
        return new Date(dateString).toLocaleDateString("en-US", { 
            month: "short", 
            day: "numeric", 
            year: "numeric" 
        });
    };

    return (
        <div className="min-h-screen bg-[#F5F0EB]">
            <div className="mx-auto max-w-7xl px-4">
                {/* Header */}
                <div className="mb-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-xl font-semibold text-black dark:text-white">Signal Management</h1>
                            <p className="text-xs text-gray-600 mt-0.5">
                                Admin interface to create and manage market signals
                            </p>
                        </div>
                        <button
                            onClick={() => navigate("/bo/signals/create")}
                            className="flex text-xs items-center gap-2 bg-black text-white px-3 py-1.5 rounded-xs hover:bg-gray-800 transition-colors"
                        >
                            <Plus className="w-4 h-4" />
                            Create Signal
                        </button>
                    </div>
                </div>

                {/* Tabs */}
                <BONavigation />

                {/* Stats Overview */}
                {stats && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                        <div className="bg-white border border-gray-200 rounded-xs p-4">
                            <p className="text-xs text-gray-600 mb-1">Total Signals</p>
                            <p className="text-xl font-semibold text-navy-700 dark:text-white">{stats.totalSignals}</p>
                        </div>
                        <div className="bg-white border border-gray-200 rounded-xs p-4">
                            <p className="text-xs text-gray-600 mb-1">Active</p>
                            <p className="text-xl font-semibold text-navy-700 dark:text-white">{stats.activeSignals}</p>
                        </div>
                        <div className="bg-white border border-gray-200 rounded-xs p-4">
                            <p className="text-xs text-gray-600 mb-1">Inactive</p>
                            <p className="text-xl font-semibold text-navy-700 dark:text-white">{stats.inactiveSignals}</p>
                        </div>
                        <div className="bg-white border border-gray-200 rounded-xs p-4">
                            <p className="text-xs text-gray-600 mb-1">Expired</p>
                            <p className="text-xl font-semibold text-navy-700 dark:text-white">{stats.expiredSignals}</p>
                        </div>
                    </div>
                )}

                {/* Search and Filters */}
                <div className="bg-white border border-gray-200 rounded-xs p-4 mb-6">
                    <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                        <div className="flex items-center gap-2 flex-1 w-full sm:w-auto">
                            <Search className="w-4 h-4 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search signals..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                onKeyPress={(e) => e.key === "Enter" && handleSearch()}
                                className="flex-1 border-0 focus:ring-0 focus:outline-none text-sm placeholder:text-gray-400"
                            />
                        </div>
                        <div className="flex gap-2">
                            <button
                                onClick={() => setShowFilters(!showFilters)}
                                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-xs border transition-colors ${
                                    showFilters
                                        ? "bg-black text-white border-black"
                                        : "bg-white text-gray-700 border-gray-200 hover:bg-[#F9F6F3]"
                                }`}
                            >
                                <Filter className="w-4 h-4" />
                                Filters
                            </button>
                        </div>
                    </div>

                    {/* Filter Panel */}
                    {showFilters && (
                        <div className="mt-4 pt-4 border-t border-gray-200">
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                                        Status
                                    </label>
                                    <select
                                        value={filters.isActive || "all"}
                                        onChange={(e) => handleFilterChange("isActive", e.target.value)}
                                        className="w-full px-2.5 py-1.5 border border-gray-200 rounded-xs bg-white text-gray-900 text-xs focus:ring-1 focus:ring-gray-900 focus:outline-none"
                                    >
                                        <option value="all">All</option>
                                        <option value="true">Active</option>
                                        <option value="false">Inactive</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                                        Event Type
                                    </label>
                                    <select
                                        value={filters.eventType || ""}
                                        onChange={(e) => handleFilterChange("eventType", e.target.value || undefined)}
                                        className="w-full px-2.5 py-1.5 border border-gray-200 rounded-xs bg-white text-gray-900 text-xs focus:ring-1 focus:ring-gray-900 focus:outline-none"
                                    >
                                        <option value="">All Types</option>
                                        {eventTypes.map(type => (
                                            <option key={type.value} value={type.value}>
                                                {type.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                                        Importance
                                    </label>
                                    <select
                                        value={filters.importance || ""}
                                        onChange={(e) => handleFilterChange("importance", e.target.value || undefined)}
                                        className="w-full px-2.5 py-1.5 border border-gray-200 rounded-xs bg-white text-gray-900 text-xs focus:ring-1 focus:ring-gray-900 focus:outline-none"
                                    >
                                        <option value="">All Levels</option>
                                        {importanceLevels.map(level => (
                                            <option key={level.value} value={level.value}>
                                                {level.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                                        Sort By
                                    </label>
                                    <select
                                        value={filters.sortBy || "createdAt"}
                                        onChange={(e) => handleFilterChange("sortBy", e.target.value)}
                                        className="w-full px-2.5 py-1.5 border border-gray-200 rounded-xs bg-white text-gray-900 text-xs focus:ring-1 focus:ring-gray-900 focus:outline-none"
                                    >
                                        <option value="createdAt">Created Date</option>
                                        <option value="publishedAt">Published Date</option>
                                        <option value="importance">Importance</option>
                                        <option value="amount">Funding Amount</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                                        Order
                                    </label>
                                    <select
                                        value={filters.order || "desc"}
                                        onChange={(e) => handleFilterChange("order", e.target.value)}
                                        className="w-full px-2.5 py-1.5 border border-gray-200 rounded-xs bg-white text-gray-900 text-xs focus:ring-1 focus:ring-gray-900 focus:outline-none"
                                    >
                                        <option value="desc">Newest First</option>
                                        <option value="asc">Oldest First</option>
                                    </select>
                                </div>
                            </div>
                            <div className="mt-3">
                                <label className="flex items-center gap-2 text-xs">
                                    <input
                                        type="checkbox"
                                        checked={filters.includeExpired || false}
                                        onChange={(e) => handleFilterChange("includeExpired", e.target.checked)}
                                        className="rounded border-gray-200"
                                    />
                                    <span className="text-gray-700">Include expired signals</span>
                                </label>
                            </div>
                        </div>
                    )}
                </div>

                {/* Signals Table */}
                {loading && page === 1 ? (
                    <div className="flex items-center justify-center h-64">
                        <div className="text-gray-500 text-sm">Loading signals...</div>
                    </div>
                ) : error ? (
                    <div className="flex items-center justify-center h-64">
                        <div className="text-red-600 text-sm">{error}</div>
                    </div>
                ) : signals.length === 0 ? (
                    <div className="text-center py-16">
                        <BarChart2 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                        <h3 className="text-base font-semibold text-navy-700 dark:text-white mb-2">
                            No signals found
                        </h3>
                        <p className="text-sm text-gray-500 mb-6">
                            Create your first signal to get started
                        </p>
                        <button
                            onClick={() => navigate("/bo/signals/create")}
                            className="bg-black text-white px-4 py-2 text-xs rounded-xs hover:bg-gray-800 transition-colors"
                        >
                            Create Signal
                        </button>
                    </div>
                ) : (
                    <>
                        <div className="bg-white border border-gray-200 rounded-xs overflow-hidden">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider">
                                            Image
                                        </th>
                                        <th className="px-4 py-3 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider">
                                            Signal
                                        </th>
                                        <th className="px-4 py-3 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider">
                                            Social
                                        </th>
                                        <th className="px-4 py-3 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider">
                                            Type
                                        </th>
                                        <th className="px-4 py-3 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider">
                                            Importance
                                        </th>
                                        <th className="px-4 py-3 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider">
                                            Rating
                                        </th>
                                        <th className="px-4 py-3 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider">
                                            Created
                                        </th>
                                        <th className="px-4 py-3 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider">
                                            Status
                                        </th>
                                        <th className="px-4 py-3 text-right text-[11px] font-semibold text-gray-600 uppercase tracking-wider">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {signals.map((signal) => (
                                        <tr key={signal._id} className="hover:bg-[#F9F6F3] transition-colors">
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                {signal.imageUrl ? (
                                                    <img
                                                        src={signal.imageUrl}
                                                        alt={signal.title}
                                                        className="h-10 w-16 object-cover rounded-xs border border-gray-200"
                                                        onError={(e) => {
                                                            e.currentTarget.style.display = 'none';
                                                        }}
                                                    />
                                                ) : (
                                                    <div className="h-10 w-16 bg-gray-100 rounded-xs border border-gray-200 flex items-center justify-center">
                                                        <span className="text-[11px] text-gray-400">No image</span>
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="flex flex-col max-w-xs">
                                                    <span className="text-xs font-semibold text-navy-700 dark:text-white truncate">
                                                        {signal.title}
                                                    </span>
                                                    <span className="text-[11px] text-gray-500 truncate">
                                                        {signal.company}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                {signal.establishment?.social_networks && signal.establishment.social_networks.length > 0 ? (
                                                    <div className="flex items-center gap-1">
                                                        {signal.establishment.social_networks.map((network, idx) => {
                                                            const getSocialIcon = (platform: string) => {
                                                                const p = platform.toLowerCase();
                                                                if (p === 'instagram') return '📷';
                                                                if (p === 'facebook') return '👥';
                                                                if (p === 'twitter' || p === 'x') return '🐦';
                                                                if (p === 'linkedin') return '💼';
                                                                if (p === 'youtube') return '📺';
                                                                if (p === 'tiktok') return '🎵';
                                                                return '🔗';
                                                            };
                                                            
                                                            return network.url ? (
                                                                <a
                                                                    key={idx}
                                                                    href={network.url}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="text-sm hover:scale-110 transition-transform"
                                                                    title={network.platform}
                                                                >
                                                                    {getSocialIcon(network.platform)}
                                                                </a>
                                                            ) : null;
                                                        })}
                                                    </div>
                                                ) : (
                                                    <span className="text-xs text-gray-400">-</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                <span className="text-xs text-gray-900">
                                                    {eventTypes.find(t => t.value === signal.eventType)?.label}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                <span className={`px-2 py-0.5 text-[11px] font-medium rounded-sm border ${
                                                    signal.importance === 'critical' ? 'bg-red-50 text-red-800 border-red-200' :
                                                    signal.importance === 'high' ? 'bg-orange-50 text-orange-800 border-orange-200' :
                                                    signal.importance === 'medium' ? 'bg-gray-100 text-gray-700 border-gray-200' :
                                                    'bg-gray-50 text-gray-800 border-gray-200'
                                                }`}>
                                                    {signal.importance}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                {signal.ratingCount && signal.ratingCount > 0 ? (
                                                    <div className="flex items-center gap-1">
                                                        <Star className="w-3 h-3 text-yellow-400 fill-yellow-400" />
                                                        <span className="text-xs font-medium text-gray-900">
                                                            {signal.averageRating?.toFixed(1)}
                                                        </span>
                                                        <span className="text-[11px] text-gray-500">
                                                            ({signal.ratingCount})
                                                        </span>
                                                    </div>
                                                ) : (
                                                    <span className="text-xs text-gray-400">No ratings</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-500">
                                                {formatDate(signal.createdAt)}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                <button
                                                    onClick={() => handleToggleActive(signal._id)}
                                                    className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded-sm border ${
                                                        signal.isActive
                                                            ? 'bg-green-50 text-green-800 border-green-200 hover:bg-green-100'
                                                            : 'bg-gray-50 text-gray-800 border-gray-200 hover:bg-gray-100'
                                                    }`}
                                                >
                                                    {signal.isActive ? (
                                                        <><CheckCircle className="w-3 h-3" /> Active</>
                                                    ) : (
                                                        <><XCircle className="w-3 h-3" /> Inactive</>
                                                    )}
                                                </button>
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-right">
                                                <div className="flex items-center justify-end gap-2">
                                                    <button
                                                        onClick={() => setSelectedSignalForRatings(signal)}
                                                        className="text-yellow-600 hover:text-yellow-800 transition-colors relative"
                                                        title="View Ratings"
                                                    >
                                                        <Star className="w-4 h-4" />
                                                        {signal.ratingCount && signal.ratingCount > 0 && (
                                                            <span className="absolute -top-1 -right-1 bg-yellow-600 text-white text-[9px] font-bold rounded-full w-3 h-3 flex items-center justify-center">
                                                                {signal.ratingCount}
                                                            </span>
                                                        )}
                                                    </button>
                                                    <button
                                                        onClick={() => navigate(`/bo/signals/edit/${signal._id}`)}
                                                        className="text-black hover:text-black/60 transition-colors"
                                                        title="Edit"
                                                    >
                                                        <Pencil className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDelete(signal._id)}
                                                        className="text-red-600 hover:text-red-800 transition-colors"
                                                        title="Delete"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Load More */}
                        {hasMore && (
                            <div className="flex justify-center mt-6">
                                <button
                                    onClick={loadMore}
                                    disabled={loading}
                                    className="px-4 py-2 bg-black hover:bg-gray-800 disabled:bg-gray-400 text-white text-xs rounded-xs transition-colors"
                                >
                                    {loading ? "Loading..." : "Load More"}
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Ratings Modal */}
            {selectedSignalForRatings && (
                <SignalRatingsModal
                    signalId={selectedSignalForRatings._id}
                    signalTitle={selectedSignalForRatings.title}
                    onClose={() => setSelectedSignalForRatings(null)}
                />
            )}
        </div>
    );
}
